import i18n, { t } from '@if/core/i18n'
import {
  CreditDecision,
  EBeneficiaryType,
  ECreditApplicationStatus,
  EFinancingProgram,
  EGender,
  EHomeStatus,
  EJobType,
  ELanguage,
  ELoanPurpose,
  EOriginSystemId,
  EOtherIncomeType,
  EPaymentPlan,
  EProgressionStatus,
  EProvince,
  getEHomeStatusList,
  getELanguageList,
} from '@if/core/types'
import * as yup from '@if/core/yup'
import { collectsExpensesAndIncome, programConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { AddressSchema, BaseAddressSchema, RequiredAddressSchema } from './AddressSchema'
import { Contract } from './Contract'
import { NormCreditDecision } from './CreditDecision'
import { ElectronicSignature } from './ElectronicSignature'
import { FilteredWorksheet } from './FilteredWorksheet'
import { PrequalificationDecision } from './PrequalificationDecision'
import { RequiredDocument } from './RequiredDocument'

export const expensesSchema = yup.object({
  financingProgramId: yup
    .mixed<EFinancingProgram>()
    .nullable()
    .default('' as EFinancingProgram),

  monthlyInstallmentLoanPayment: yup.number().min(0).nullable(),
  monthlyStudentLoanPayment: yup.number().min(0).nullable(),
  monthlyLineOfCreditPayment: yup.number().min(0).nullable(),
  monthlyCreditCardsPayment: yup.number().min(0).nullable(),
  monthlyPaydayLoanPayment: yup.number().min(0).nullable(),
  monthlyPersonalLoanPayment: yup.number().min(0).nullable(),
  monthlyOtherLoanPayment: yup.number().min(0).nullable(),

  monthlyOtherExpensePayment: yup.number().positive().nullable(),
  otherExpensePaymentDescription: yup
    .text()
    .nullable()
    .max(50)
    .when('monthlyOtherExpensePayment', (val) => {
      return val && (val as unknown as number) > 0 ? yup.text().required().default('') : yup.text().notRequired()
    }),

  homeFeeTypeId: yup
    .mixed<EHomeStatus>()
    .oneOf([undefined, ...getEHomeStatusList()])
    .default(null)
    .nullable(),

  houseMarketValue: yup.double().min(1, () => t('common.errors.positive')),

  rentMonthly: yup.double(),
  mortgageMonthly: yup.double(),

  housingMonthly: yup.integer().when('homeFeeTypeId', {
    is: (value: EHomeStatus) => value === EHomeStatus.Renter,
    then: (schema) => schema.required().min(1),
    otherwise: (schema) => schema.notRequired().positive(),
  }),

  landlord: yup.text().nullable().default(''),

  monthlyOwnerInsuranceFee: yup.double().min(0),
  monthlyMortgageInsuranceFee: yup.double().min(0),
  monthlyMunicipalAndSchoolTaxPayment: yup.double().min(0),
  monthlyCondominiumFee: yup.double().min(0),
  monthlyPublicServiceFee: yup.double().min(0),
  monthlyRrspRefund: yup.double().min(0),
  monthlyOtherHomeFee: yup.double().min(0),

  isCompleted: yup.bool().nullable(),
})

export type ApplicantExpenses = yup.InferSubmit<typeof expensesSchema>

export const ExpensesSchema = expensesSchema.when((value: ApplicantExpenses[], schema) => {
  if (value[0]?.houseMarketValue || value[0]?.housingMonthly) {
    schema.fields.homeFeeTypeId = yup.mixed<EHomeStatus>().oneOf(getEHomeStatusList()).required()
  }
  return schema
})

const RequiredHousingExpensesSchema = () =>
  ExpensesSchema.shape({
    homeFeeTypeId: yup.mixed<EHomeStatus>().oneOf(getEHomeStatusList()).required(),
    housingMonthly: yup.integer().when('homeFeeTypeId', ([homeFeeTypeId], schema) => {
      if (homeFeeTypeId === EHomeStatus.Owner) return schema.notRequired().positive()
      return schema.required().min(homeFeeTypeId === EHomeStatus.Renter ? 1 : 0)
    }),
  })

const optionalMonths = () => yup.number().nullable().optional()

const monthsUnderOneYear = () => optionalMonths().required().min(1)

const ApplicantAddressSchema = (addressSchema: typeof AddressSchema = AddressSchema) =>
  addressSchema.shape({
    years: yup.integer().required().min(0),
    months: optionalMonths().when(['years', '$useExactMonthsUnderOneYear'], ([years, useExactMonths], schema) =>
      useExactMonths && Number(years) === 0 ? monthsUnderOneYear() : schema,
    ),
  })

export type EmployerPhoneRequirement = 'none' | 'fullOrPartTime' | 'always'

export const JobSchema = (
  useExactMonthsUnderOneYear = false,
  requireCompleteAddress = false,
  employerPhone: EmployerPhoneRequirement = 'fullOrPartTime',
) =>
  yup.object({
    id: yup.text().default(''),
    employerName: yup.text().required().default(''),
    jobTitle: yup.text().required().default(''),
    jobType: yup.numberEnum<EJobType>().required().default(null),
    annualSalary: yup
      .double()
      .required()
      .integer(() => t('common.errors.integer'))
      .min(1, () => t('common.errors.positive'))
      .default(null),
    employerPhone: yup
      .text()
      .nullable()
      .when('jobType', {
        is: (jobType: EJobType) =>
          employerPhone === 'always' ||
          (employerPhone === 'fullOrPartTime' && (jobType === EJobType.FullTime || jobType === EJobType.PartTime)),
        then: (schema) => schema.required().validCanadianPhone(),
        otherwise: (schema) => schema.validCanadianPhone(),
      }),
    employerPhoneExt: yup.text().validPhoneExtension().nullable(),
    address: (requireCompleteAddress ? AddressSchema : BaseAddressSchema).nullable(),
    years: yup.integer().required().min(0),
    months: optionalMonths().when('years', ([years], schema) =>
      useExactMonthsUnderOneYear && Number(years) === 0 ? monthsUnderOneYear() : schema,
    ),
    verificationContactName: yup.text().nullable(),
    verificationContactRole: yup.text().nullable(),
    verificationContactPhone: yup.text().nullable(),
    isIncomeLocked: yup.bool(),
  })

export type ApplicantJob = yup.InferSubmit<ReturnType<typeof JobSchema>>

export const OtherIncomeSchema = yup.object({
  id: yup.text().default(''),
  typeId: yup.integer().required(),
  description: yup
    .text()
    .nullable()
    .when('typeId', (value) => {
      return value[0] === EOtherIncomeType.Other ? yup.text().required() : yup.text().notRequired()
    }),
  annualAmount: yup
    .integer()
    .required()
    .when('typeId', (value) => {
      return value[0] === EOtherIncomeType.IncomeAdjustmentToMatchStatements
        ? yup.integer().required()
        : yup.integer().required().positive().min(0)
    }),
  isIncomeLocked: yup.bool(),
})
export type ApplicantOtherIncome = yup.InferSubmit<typeof OtherIncomeSchema>

export interface IncomeVerification {
  thirdPartyFlowCompleted: boolean
  incomeMismatch: boolean
  nameMismatch: boolean
  hasPendingFraudAlert: boolean
  allIncomeTasksConfirmed: boolean
  hasIncomeServiceAdjustment: boolean
  hasSkippedIncomeValidation: boolean
  thirdPartyIncome: number
  declaredIncome: number
  requiredExternalStep: RequiredExternalStep
}

export interface RequiredExternalStep {
  applicantType: string
  status: string
}

const draftApplicantSchema = () => ({
  id: yup.text().default(null), // le default null est nécessaire pour le createEntityAdapter
  financingProgramId: yup.mixed<EFinancingProgram>(),
  middleName: yup.text().validCharacters().max(50).default('').nullable(),
  relationWithApplicant: yup.text().when('isPrimaryApplicant', (value) => {
    return !value[0] ? yup.text().required().default('') : yup.text().default('')
  }),
  hasLegalName: yup.bool(),
  firstName: yup.text().validCharacters().max(50).required().default(''),
  lastName: yup.text().when('hasLegalName', {
    is: true,
    then: (schema) => schema.oneOf(['']).max(0).default(''),
    otherwise: (schema) => schema.validCharacters().max(50).required().default(''),
  }),
  birthDate: yup.dateOnly().validAgeRequirement(yup.ref<EProvince | null | undefined>('currentAddress.stateIso')),
  sin: yup.text().validSIN().nullable().default(''),
  languageId: yup
    .numberEnum<ELanguage>(getELanguageList())
    .default(() => (i18n.resolvedLanguage === 'fr' ? ELanguage.French : ELanguage.English))
    .required(),
  genderId: yup.numberEnum<EGender>().default(null),
  maritalStatusId: yup.integer(),
  homePhone: yup.text().default('').validCanadianPhone().nullable(),
  cellPhone: yup.text().default('').validCanadianPhone().nullable(),
  email: yup.text().email().max(60).default(''),
  // Incomes
  currentJobs: yup.array(JobSchema(false, false, 'none')).default([]),
  previousJobs: yup.array(JobSchema()).default([]),
  otherIncomes: yup.array(OtherIncomeSchema).default([]),
  // address
  currentAddress: ApplicantAddressSchema(),
  expenses: ExpensesSchema.notRequired().default(ExpensesSchema.getDefault()),
  isPrimaryApplicant: yup.bool().default(true),
  isApplicantNewToCredit: yup.bool(),
  hasSin: yup.bool(),
})

export const DraftApplicantSchema = () => yup.object(draftApplicantSchema())

const fullApplicantSchema = (financingProgramId?: EFinancingProgram) => {
  const schema = { ...draftApplicantSchema() }
  schema.birthDate = schema.birthDate.required()
  schema.genderId = schema.genderId.required()
  schema.languageId = schema.languageId.required()
  schema.maritalStatusId = schema.maritalStatusId.required()
  schema.cellPhone = schema.cellPhone.required()
  schema.email = schema.email.required()
  schema.currentJobs = yup.array(JobSchema(false, true, 'always')).default([])
  schema.currentAddress = ApplicantAddressSchema(RequiredAddressSchema)
  schema.expenses = schema.expenses.when('currentAddress.stateIso', ([stateIso], expenses) =>
    collectsExpensesAndIncome(financingProgramId, stateIso as EProvince | null)
      ? RequiredHousingExpensesSchema().notRequired().default(ExpensesSchema.getDefault())
      : expenses,
  )
  return schema
}

const FullApplicantSchema = (financingProgramId?: EFinancingProgram) =>
  yup.object(fullApplicantSchema(financingProgramId))

export interface CreditAlertsDto extends Record<string, boolean> {
  hasNameMismatch: boolean
  hasDoBMismatch: boolean
  hasPreviousApplication: boolean
  hasCreditLocked: boolean
  hasInvalidCity: boolean
  hasGeneralCreditReportError: boolean
  isBanned: boolean
}

export type ApplicantDto = yup.InferSubmit<ReturnType<typeof DraftApplicantSchema>> & {
  incomeVerification?: IncomeVerification
  identityVerification?: RequiredExternalStep | null
  hardHitReportReceivedOn?: Date
  creditAlerts?: CreditAlertsDto
  hasCreditReport?: boolean
  isApplicantNewToCredit: boolean
  hasSin: boolean
}
export const buildDraftCreditApplicationSchemaObject = () => {
  const draftCreditApplicationSchema = {
    id: yup.text().default(''),
    financingProgramId: yup.mixed<EFinancingProgram>(),
    loanPurposeId: yup
      .text()
      .nullable()
      .when('financingProgramId', ([financingProgramId], schema) => {
        const config = programConfigByFinancingProgramId(financingProgramId as EFinancingProgram)
        if (config?.loanPurposeIdIsRequired ?? true) {
          return schema.required()
        }
        return yup.text().notRequired().nullable()
      })
      .default(null),
    requestedLoanAmount: yup.double().when('financingProgramId', (financingProgramId, schema) => {
      const value = financingProgramId[0] as EFinancingProgram
      const config = programConfigByFinancingProgramId(value)
      if (config?.requestedLoanAmountIsRequired ?? true) {
        return schema.required().min(yup.ref('$minLoanValue')).max(yup.ref('$maxLoanValue'))
      }

      return yup.double()
    }),
    merchantId: yup.text().required().default(''),
    status: yup.mixed<ECreditApplicationStatus>().required().default(ECreditApplicationStatus.Draft),
    applicant: DraftApplicantSchema().required(),
    coApplicant: DraftApplicantSchema().nullable().default(null),
    editLocked: yup.bool(),
    correspondanceLanguageId: yup.number(),
    versionTag: yup.text(),
    beneficiaryTypeId: yup
      .integer()
      .nullable()
      .when('financingProgramId', ([financingProgramId], schema) => {
        const config = programConfigByFinancingProgramId(financingProgramId as EFinancingProgram)
        if (config?.beneficiaryIsRequired ?? true) {
          return schema
            .required()
            .oneOf([0, EBeneficiaryType.Applicant, EBeneficiaryType.Coapplicant, EBeneficiaryType.Other])
            .default(EBeneficiaryType.Applicant)
        }
        return yup.integer().nullable()
      })
      .default(null),
    otherBeneficiaryFirstName: yup
      .text()
      .trim()
      .when('loanPurposeId', (loanPurposeId, schema) => {
        if (loanPurposeId[0] === ELoanPurpose.Veterinary || loanPurposeId[0] === ELoanPurpose.GoodsAndServices) {
          return schema.notRequired().nullable()
        }
        return schema.when('beneficiaryTypeId', (value) => {
          return value[0] === EBeneficiaryType.Other ? yup.text().required() : yup.text().notRequired().nullable()
        })
      }),

    otherBeneficiaryLastName: yup
      .text()
      .trim()
      .when('loanPurposeId', (loanPurposeId, schema) => {
        if (loanPurposeId[0] === ELoanPurpose.GoodsAndServices) {
          return schema.notRequired().nullable()
        }
        return schema.when('beneficiaryTypeId', (value) => {
          return value[0] === EBeneficiaryType.Other ? yup.text().required() : yup.text().notRequired().nullable()
        })
      }),
    originSystemId: yup.mixed<EOriginSystemId>().required().default(EOriginSystemId.MerchantDashboard),
    consentSoftHit: yup.bool(),
    consentHardHit: yup.bool(),
    isSinglePayment: yup.bool(),
    isInvisalign: yup.bool(),
    isSymTechPrime: yup.bool(),
    merchantReferenceCode: yup.text().max(20),
    merchantPaymentPlanId: yup.mixed<EPaymentPlan>().nullable().default(null),
    prequalPromoCode: yup.text(),
  }
  return yup.object(draftCreditApplicationSchema).test(
    'test-coappSameMobileError',
    () => t('common.errors.coappSameMobileError'),
    function distinctCellphoneTest(value) {
      const { createError } = this
      if (value?.coApplicant !== null) {
        const coappCellPhone = value.coApplicant.cellPhone
        const appCellphone = value.applicant.cellPhone
        if (appCellphone && coappCellPhone) {
          return (
            appCellphone !== coappCellPhone ||
            createError({ path: 'coApplicant.cellPhone', message: t('common.errors.coappSameMobileError') })
          )
        }
      }
      return true
    },
  )
}
export const buildDraftCreditApplicationSchema = () => {
  return buildDraftCreditApplicationSchemaObject()
}
export const DraftCreditApplicationSchema = () => buildDraftCreditApplicationSchema()

export const buildFullCreditApplicationSchema = (financingProgramId?: EFinancingProgram) => {
  const schema = buildDraftCreditApplicationSchemaObject().shape({
    merchantId: yup.text().required(),
    applicant: FullApplicantSchema(financingProgramId).required(),
    coApplicant: FullApplicantSchema(financingProgramId).nullable().default(null),
  })
  return schema
}

export const FullCreditApplicationSchema = () => buildFullCreditApplicationSchema()

export type DraftCreditApplicationDto = yup.InferSubmit<ReturnType<typeof DraftCreditApplicationSchema>>

type EditCreditApplicationDto = yup.InferSubmit<ReturnType<typeof FullCreditApplicationSchema>>

export type CreditApplication = EditCreditApplicationDto & {
  id: string
  applicant: ApplicantDto
  coApplicant: ApplicantDto | null
  currentRevisionId: string
  referenceNumber: number
  parentReferenceNumber: number | null
  parentCreditApplicationId: string | null
  normsCreditDecision: NormCreditDecision | null
  finalCreditDecision: CreditDecision & { isHardDecline: boolean }
  createdOn: Date
  updatedOn: Date
  expiresOn: Date
  deliveryOn: string
  createdByUserFullName: string
  correspondanceLanguageId: number
  cvtNumber: number | null
  loanId: number | null
  loanPurposeId: ELoanPurpose
  prequalificationDecision: PrequalificationDecision | null
  merchantPaymentPlanId: EPaymentPlan
  prequalPromoCode: string | null
  allIncomesConfirmed: boolean
  requiredDocuments: RequiredDocument[]
  loanCreated: boolean
  beneficiaryTypeId: number
  otherBeneficiaryFirstName: string | null
  otherBeneficiaryLastName: string | null
  consentSoftHit: boolean
  consentHardHit: boolean
  isSinglePayment: boolean
  isInvisalign: boolean
  isSymTechPrime: boolean
  electronicSignatures: ElectronicSignature[]
  financingProgramId: EFinancingProgram
  worksheet: FilteredWorksheet | null
  contract: Contract
  hasTasksBeforeCvt: boolean
  availableCreditAmount: number
  hasOngoingElectronicSignature: boolean
  progressionStatus: EProgressionStatus
  merchantReferenceCode: string | null
  note: string | null
  merchantDocumentUploadRequestSentOn: Date | null
}
const editApplicantContactInfoDtoSchema = {
  id: yup.text().default('').required(),
  cellPhone: yup.text().default('').validCanadianPhone(),
  email: yup.text().email().max(60).default(''),
  applicantType: yup.text(),
  financingProgramId: yup.mixed<EFinancingProgram>().required(),
  languageId: yup
    .numberEnum<ELanguage>(getELanguageList())
    .nullable()
    .default(() => (i18n.resolvedLanguage === 'fr' ? ELanguage.French : ELanguage.English))
    .required(),
}

export const EditApplicantContactInfoDtoSchema = yup.object(editApplicantContactInfoDtoSchema)

export type EditApplicantContactInfoDto = yup.InferSubmit<typeof EditApplicantContactInfoDtoSchema>
